import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {AttachmentClassificationRequest} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SidebarLinkAttachmentClassificationsAdmin} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  useCreateAttachmentClassificationMutation,
  useGetAttachmentClassificationQuery,
  useUpdateAttachmentClassificationMutation,
} from 'src/redux/queries/attachmentClassification';
import {
  ATTACHMENT_CLASSIFICATION_FORM_META,
  GetDefaultAttachmentClassificationFormData,
  MapAttachmentClassificationResponseToFormData,
} from './attachmentClassificationHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Attachment Classification',
  entityNamePlural: 'Attachment Classifications',
  listPath: PathUtility.AttachmentClassification(),
  viewPath: id => PathUtility.AttachmentClassificationView(id),
};

export interface AttachmentClassificationFormProps {
  id?: number;
  mode: 'create' | 'view' | 'edit';
}

const applyReadOnly = (fields: typeof ATTACHMENT_CLASSIFICATION_FORM_META) =>
  fields.map(f => ({...f, readOnly: true}));

export const AttachmentClassificationForm: React.FC<
  AttachmentClassificationFormProps
> = ({id, mode}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);

  const perms = useModulePermissions(ModuleEnum.AttachmentClassifications);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const {data: classificationData, isLoading} =
    useGetAttachmentClassificationQuery(id!, {skip: id === undefined});

  const [createMutation] = useCreateAttachmentClassificationMutation();
  const [updateMutation] = useUpdateAttachmentClassificationMutation();

  const formMeta = useMemo(
    () =>
      isViewMode
        ? applyReadOnly(ATTACHMENT_CLASSIFICATION_FORM_META)
        : ATTACHMENT_CLASSIFICATION_FORM_META,
    [isViewMode]
  );

  const form = useEntitySyncedForm({
    formMeta,
    initialData: GetDefaultAttachmentClassificationFormData(),
    entity: classificationData,
    toFormData: MapAttachmentClassificationResponseToFormData,
  });

  const name = useWatch({control: form.methods.control, name: 'name'});

  const handleSave = async () => {
    try {
      setIsSavePending(true);

      const values = form.methods.getValues();
      const body: AttachmentClassificationRequest = {
        id: id,
        name: values.name as string,
        description: values.description as string | undefined,
      };

      const res =
        id === undefined
          ? await createMutation(body).unwrap()
          : await updateMutation(body).unwrap();

      setAlert(
        id === undefined
          ? `${values.name} was successfully created`
          : `${values.name} was successfully updated`,
        'success'
      );

      navigate(PathUtility.AttachmentClassificationView(String(res.id)), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
    }
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.AttachmentClassificationView(id)
      : PathUtility.AttachmentClassification()
  );

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: name as string,
      }),
    [mode, id, name]
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const saveButtons = (
    <>
      <PageHeader.Action variant="cancel" onClick={handleCancel}>
        Cancel
      </PageHeader.Action>
      <PageHeader.Action
        variant={isCreateMode ? 'create' : 'save'}
        onClick={handleValidateAndSave}
        loading={isSavePending}>
        {isCreateMode ? 'Create' : 'Save Changes'}
      </PageHeader.Action>
    </>
  );

  return (
    <Stack gap="lg">
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkAttachmentClassificationsAdmin.icon}>
          {isCreateMode
            ? 'New Attachment Classification'
            : (name as string) || 'Attachment Classification'}
        </PageHeader.Title>

        <PageHeader.Actions>
          {isViewMode && perms.update && (
            <PageHeader.Action
              variant="edit"
              onClick={() =>
                navigate(PathUtility.AttachmentClassificationEdit(id!))
              }>
              Edit
            </PageHeader.Action>
          )}

          {!isViewMode && saveButtons}
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <Stack gap="md">
          <SectionCard title="General" data-testid="section-general">
            <Box p="md">
              <Grid gutter="md">
                <ProactField name="name" />
                <ProactField name="description" />
              </Grid>
            </Box>
          </SectionCard>

          {!isViewMode && <Group mt="md">{saveButtons}</Group>}
        </Stack>
      </ProactForm>
    </Stack>
  );
};
